import React, { useEffect, useState } from "react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { ToastContainer, toast } from "react-toastify"
import ReactPaginate from "react-paginate"
import { URLS } from "../../Url"
import axios from "axios"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Label,
  Form,
  FormGroup,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputGroupText,
} from "reactstrap"

const PaymentsAccounts = () => {
  const GetAuth = localStorage.getItem("authUser")
  const TokenJson = JSON.parse(GetAuth)
  const TokenData = TokenJson.token

  const initialFormState = {
    accountHolderName: "",
    bankName: "",
    accountNumber: "",
    branch: "",
    ifscCode: "",
    accountType: "",
    upiId: "",
    cardNumber: "",
    cardExpiryDate: "",
    cvv: "",
    nameOnCard: "",
    type: "",
    defaultPayment: false,
  }

  const [form, setForm] = useState(initialFormState)
  const [editForm, setEditForm] = useState(initialFormState)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [data, setData] = useState([])
  const [listPerPage] = useState(10)
  const [pageNumber, setPageNumber] = useState(0)
  const [errors, setErrors] = useState({})
  const [editErrors, setEditErrors] = useState({})
  const [cardType, setCardType] = useState("")

  useEffect(() => {
    fetchPaymentAccounts()
  }, [])

  const fetchPaymentAccounts = () => {
    axios
      .post(
        URLS.GetRecepitAccount,
        {},
        {
          headers: { Authorization: `Bearer ${TokenData}` },
        }
      )
      .then(res => {
        setData(res.data.receiptaccounts || [])
      })
      .catch(error => {
        toast.error("Failed to fetch payment accounts")
        console.error("Fetch error:", error)
      })
  }

  // Enhanced card validation functions
  const getCardType = cardNumber => {
    // Remove any non-digit characters
    const cleanedCardNumber = cardNumber.replace(/\D/g, "")

    // Visa
    if (/^4/.test(cleanedCardNumber)) return "visa"

    // Mastercard
    if (/^(5[1-5]|2[2-7])/.test(cleanedCardNumber)) return "mastercard"

    // American Express
    if (/^3[47]/.test(cleanedCardNumber)) return "amex"

    // Discover
    if (/^(6011|65|64[4-9]|622)/.test(cleanedCardNumber)) return "discover"

    // Diners Club
    if (/^(36|38|30[0-5])/.test(cleanedCardNumber)) return "diners"

    // JCB
    if (/^35/.test(cleanedCardNumber)) return "jcb"

    return "unknown"
  }

  const formatCardNumber = value => {
    // Remove all non-digits
    const v = value.replace(/\D/g, "")

    // Based on card type, format differently
    const cardType = getCardType(v)

    if (cardType === "amex") {
      // AMEX: 4-6-5 format
      return v.replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3")
    } else {
      // Others: 4-4-4-4 format
      return v.replace(/(\d{4})(?=\d)/g, "$1 ")
    }
  }

  const validateCardNumber = cardNumber => {
    // Remove any spaces or dashes
    const cleanedCardNumber = cardNumber.replace(/[\s-]/g, "")

    // Check if it's all digits and correct length
    if (!/^\d+$/.test(cleanedCardNumber)) return false

    // Check length based on card type
    const type = getCardType(cleanedCardNumber)
    if (type === "amex" && cleanedCardNumber.length !== 15) return false
    if (type !== "amex" && cleanedCardNumber.length !== 16) return false

    // Luhn algorithm validation
    let sum = 0
    let isEven = false

    for (let i = cleanedCardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cleanedCardNumber.charAt(i), 10)

      if (isEven) {
        digit *= 2
        if (digit > 9) {
          digit -= 9
        }
      }

      sum += digit
      isEven = !isEven
    }

    return sum % 10 === 0
  }

  const validateExpiryDate = expiryDate => {
    if (!expiryDate) return false

    const [year, month] = expiryDate.split("-")
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1 // Months are 0-indexed

    // Check if the expiry date is in the future
    if (parseInt(year) < currentYear) return false
    if (parseInt(year) === currentYear && parseInt(month) < currentMonth)
      return false

    return true
  }

  const validateCvv = (cvv, cardNumber) => {
    if (!cvv) return false

    const cardType = getCardType(cardNumber.replace(/\s/g, ""))

    // CVV should be 3 digits for most cards, 4 digits for American Express
    if (cardType === "amex") {
      return /^[0-9]{4}$/.test(cvv)
    }
    return /^[0-9]{3}$/.test(cvv)
  }

  const validateUpiId = upiId => {
    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/
    return upiRegex.test(upiId)
  }

  const validateIfscCode = ifscCode => {
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/
    return ifscRegex.test(ifscCode)
  }

  const validateAccountNumber = accountNumber => {
    // Account number should be between 9 and 18 digits
    return /^[0-9]{9,18}$/.test(accountNumber)
  }

  const validateForm = (formData, isEdit = false) => {
    const newErrors = {}

    if (!formData.type) {
      newErrors.type = "Payment type is required"
    }

    if (
      formData.type === "Upi" ||
      formData.type === "Gpay" ||
      formData.type === "Phonepe"
    ) {
      if (!formData.upiId) {
        newErrors.upiId = "UPI ID is required"
      } else if (!validateUpiId(formData.upiId)) {
        newErrors.upiId = "Please enter a valid UPI ID (e.g., name@bank)"
      }
    }

    if (formData.type === "Creditcard" || formData.type === "Debitcard") {
      if (!formData.cardNumber) {
        newErrors.cardNumber = "Card number is required"
      } else if (!validateCardNumber(formData.cardNumber)) {
        newErrors.cardNumber = "Please enter a valid card number"
      }

      if (!formData.cardExpiryDate) {
        newErrors.cardExpiryDate = "Expiry date is required"
      } else if (!validateExpiryDate(formData.cardExpiryDate)) {
        newErrors.cardExpiryDate = "Card has expired or invalid date"
      }

      if (!formData.cvv) {
        newErrors.cvv = "CVV is required"
      } else if (!validateCvv(formData.cvv, formData.cardNumber)) {
        const cardType = getCardType(formData.cardNumber.replace(/\s/g, ""))
        newErrors.cvv =
          cardType === "amex"
            ? "Please enter a valid 4-digit CVV"
            : "Please enter a valid 3-digit CVV"
      }

      if (!formData.nameOnCard) {
        newErrors.nameOnCard = "Name on card is required"
      }
    }

    if (formData.type === "Bankaccount") {
      if (!formData.bankName) {
        newErrors.bankName = "Bank name is required"
      }

      if (!formData.accountHolderName) {
        newErrors.accountHolderName = "Account holder name is required"
      }

      if (!formData.accountNumber) {
        newErrors.accountNumber = "Account number is required"
      } else if (!validateAccountNumber(formData.accountNumber)) {
        newErrors.accountNumber =
          "Please enter a valid account number (9-18 digits)"
      }

      if (!formData.branch) {
        newErrors.branch = "Branch is required"
      }

      if (!formData.ifscCode) {
        newErrors.ifscCode = "IFSC code is required"
      } else if (!validateIfscCode(formData.ifscCode.toUpperCase())) {
        newErrors.ifscCode =
          "Please enter a valid IFSC code (e.g., SBIN0123456)"
      }

      if (!formData.accountType) {
        newErrors.accountType = "Account type is required"
      }
    }

    if (isEdit) {
      setEditErrors(newErrors)
    } else {
      setErrors(newErrors)
    }

    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = e => {
    const { name, value } = e.target

    if (name === "cardNumber") {
      const formattedValue = formatCardNumber(value)
      setForm(prev => ({ ...prev, [name]: formattedValue }))
      setCardType(getCardType(value.replace(/\s/g, "")))
    } else {
      setForm(prev => ({ ...prev, [name]: value }))
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  const handleEditInputChange = e => {
    const { name, value } = e.target

    if (name === "cardNumber") {
      const formattedValue = formatCardNumber(value)
      setEditForm(prev => ({ ...prev, [name]: formattedValue }))
    } else {
      setEditForm(prev => ({ ...prev, [name]: value }))
    }

    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  const handleCheckboxChange = e => {
    const { name, checked } = e.target
    setForm(prev => ({ ...prev, [name]: checked }))
  }

  const handleEditCheckboxChange = e => {
    const { name, checked } = e.target
    setEditForm(prev => ({ ...prev, [name]: checked }))
  }

  const handleAddSubmit = e => {
    e.preventDefault()
    if (validateForm(form)) {
      addPaymentAccount()
    }
  }

  const addPaymentAccount = () => {
    axios
      .post(URLS.AddRecepitAccount, form, {
        headers: { Authorization: `Bearer ${TokenData}` },
      })
      .then(res => {
        if (res.status === 200) {
          toast.success(res.data.message)
          setShowAddForm(false)
          setForm(initialFormState)
          setErrors({})
          setCardType("")
          fetchPaymentAccounts()
        }
      })
      .catch(error => {
        const errorMsg =
          error.response?.data?.message || "Failed to add payment account"
        toast.error(errorMsg)
      })
  }

  const handleEditSubmit = e => {
    e.preventDefault()
    if (validateForm(editForm, true)) {
      updatePaymentAccount()
    }
  }

  const updatePaymentAccount = () => {
    axios
      .put(URLS.EditRecepitAccount + editForm._id, editForm, {
        headers: { Authorization: `Bearer ${TokenData}` },
      })
      .then(res => {
        if (res.status === 200) {
          toast.success(res.data.message)
          setShowEditModal(false)
          setEditErrors({})
          fetchPaymentAccounts()
        }
      })
      .catch(error => {
        const errorMsg =
          error.response?.data?.message || "Failed to update payment account"
        toast.error(errorMsg)
      })
  }

  const deletePaymentAccount = account => {
    const confirmBox = window.confirm(
      "Do you really want to delete this payment account?"
    )
    if (confirmBox === true) {
      axios
        .delete(URLS.DeleteRecepitAccount + account._id, {
          headers: { Authorization: `Bearer ${TokenData}` },
        })
        .then(res => {
          if (res.status === 200) {
            toast.success(res.data.message)
            fetchPaymentAccounts()
          }
        })
        .catch(error => {
          const errorMsg =
            error.response?.data?.message || "Failed to delete payment account"
          toast.error(errorMsg)
        })
    }
  }

  const openEditModal = account => {
    setEditForm({
      _id: account._id,
      accountHolderName: account.accountHolderName,
      bankName: account.bankName,
      accountNumber: account.accountNumber,
      branch: account.branch,
      ifscCode: account.ifscCode,
      accountType: account.accountType,
      upiId: account.upiId,
      cardNumber: account.cardNumber,
      cardExpiryDate: account.cardExpiryDate,
      cvv: account.cvv,
      nameOnCard: account.nameOnCard,
      type: account.type,
      defaultPayment: account.defaultPayment,
    })
    setEditErrors({})
    setShowEditModal(true)
  }

  const pagesVisited = pageNumber * listPerPage
  const displayedAccounts = data.slice(pagesVisited, pagesVisited + listPerPage)
  const pageCount = Math.ceil(data.length / listPerPage)

  const changePage = ({ selected }) => {
    setPageNumber(selected)
  }

  const getCardIcon = type => {
    switch (type) {
      case "visa":
        return "fab fa-cc-visa text-primary"
      case "mastercard":
        return "fab fa-cc-mastercard text-danger"
      case "amex":
        return "fab fa-cc-amex text-info"
      case "discover":
        return "fab fa-cc-discover text-warning"
      default:
        return "far fa-credit-card"
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Bizzmate Supplier"
            breadcrumbItem="Receipt Accounts"
          />

          {/* Add Receipt Account Form */}
          <Row>
            <Col md={12}>
              {showAddForm && (
                <>
                  <Card>
                    <CardBody>
                      <h5 className="mb-4">Add New Receipt Account</h5>
                      <Form onSubmit={handleAddSubmit}>
                        <Row>
                          <Col md={12}>
                            <FormGroup>
                              <Label>
                                Payment Type
                                <span className="text-danger">*</span>
                              </Label>
                              <Input
                                type="select"
                                name="type"
                                value={form.type}
                                onChange={handleInputChange}
                                className={errors.type ? "is-invalid" : ""}
                              >
                                <option value="">Select Payment Type</option>
                                <option value="Upi">UPI</option>
                                <option value="Gpay">Google Pay</option>
                                <option value="Phonepe">Phonepe</option>
                                <option value="Creditcard">Credit Card</option>
                                <option value="Debitcard">Debit Card</option>
                                <option value="Bankaccount">
                                  Bank Account
                                </option>
                              </Input>
                              {errors.type && (
                                <div className="invalid-feedback">
                                  {errors.type}
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          {(form.type === "Upi" ||
                            form.type === "Gpay" ||
                            form.type === "Phonepe") && (
                            <Col md={6}>
                              <FormGroup>
                                <Label>
                                  {form.type}
                                  {form.type == "Upi" ? "UPI" : ""} Id
                                  <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  type="text"
                                  placeholder="e.g., name@bank"
                                  name="upiId"
                                  value={form.upiId}
                                  onChange={handleInputChange}
                                  className={errors.upiId ? "is-invalid" : ""}
                                />
                                {errors.upiId && (
                                  <div className="invalid-feedback">
                                    {errors.upiId}
                                  </div>
                                )}
                                <small className="form-text text-muted">
                                  Format: name@bank
                                </small>
                              </FormGroup>
                            </Col>
                          )}
                          {(form.type === "Creditcard" ||
                            form.type === "Debitcard") && (
                            <>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Card Number
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <InputGroup>
                                    <Input
                                      type="text"
                                      placeholder="Enter Card Number"
                                      name="cardNumber"
                                      value={form.cardNumber}
                                      onChange={handleInputChange}
                                      className={
                                        errors.cardNumber ? "is-invalid" : ""
                                      }
                                      maxLength={cardType === "amex" ? 17 : 19}
                                    />
                                    {cardType && (
                                      <InputGroupText>
                                        <i
                                          className={getCardIcon(cardType)}
                                        ></i>
                                      </InputGroupText>
                                    )}
                                  </InputGroup>
                                  {errors.cardNumber && (
                                    <div className="invalid-feedback">
                                      {errors.cardNumber}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col md={3}>
                                <FormGroup>
                                  <Label>
                                    Expiry Date
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="month"
                                    name="cardExpiryDate"
                                    value={form.cardExpiryDate}
                                    onChange={handleInputChange}
                                    className={
                                      errors.cardExpiryDate ? "is-invalid" : ""
                                    }
                                  />
                                  {errors.cardExpiryDate && (
                                    <div className="invalid-feedback">
                                      {errors.cardExpiryDate}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col md={3}>
                                <FormGroup>
                                  <Label>
                                    CVV <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="password"
                                    placeholder={
                                      cardType === "amex"
                                        ? "4 digits"
                                        : "3 digits"
                                    }
                                    name="cvv"
                                    value={form.cvv}
                                    onChange={handleInputChange}
                                    className={errors.cvv ? "is-invalid" : ""}
                                    maxLength={cardType === "amex" ? 4 : 3}
                                  />
                                  {errors.cvv && (
                                    <div className="invalid-feedback">
                                      {errors.cvv}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Name on Card
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="Enter Name as on Card"
                                    name="nameOnCard"
                                    value={form.nameOnCard}
                                    onChange={handleInputChange}
                                    className={
                                      errors.nameOnCard ? "is-invalid" : ""
                                    }
                                  />
                                  {errors.nameOnCard && (
                                    <div className="invalid-feedback">
                                      {errors.nameOnCard}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                            </>
                          )}
                          {form.type === "Bankaccount" && (
                            <>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Bank Name
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="Enter Bank Name"
                                    name="bankName"
                                    value={form.bankName}
                                    onChange={handleInputChange}
                                    className={
                                      errors.bankName ? "is-invalid" : ""
                                    }
                                  />
                                  {errors.bankName && (
                                    <div className="invalid-feedback">
                                      {errors.bankName}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Account Holder Name
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="Enter Account Holder Name"
                                    name="accountHolderName"
                                    value={form.accountHolderName}
                                    onChange={handleInputChange}
                                    className={
                                      errors.accountHolderName
                                        ? "is-invalid"
                                        : ""
                                    }
                                  />
                                  {errors.accountHolderName && (
                                    <div className="invalid-feedback">
                                      {errors.accountHolderName}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Account Number
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="Enter Account Number"
                                    name="accountNumber"
                                    value={form.accountNumber}
                                    onChange={handleInputChange}
                                    className={
                                      errors.accountNumber ? "is-invalid" : ""
                                    }
                                    maxLength="18"
                                  />
                                  {errors.accountNumber && (
                                    <div className="invalid-feedback">
                                      {errors.accountNumber}
                                    </div>
                                  )}
                                  <small className="form-text text-muted">
                                    9-18 digits only
                                  </small>
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Branch
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="Enter Branch"
                                    name="branch"
                                    value={form.branch}
                                    onChange={handleInputChange}
                                    className={
                                      errors.branch ? "is-invalid" : ""
                                    }
                                  />
                                  {errors.branch && (
                                    <div className="invalid-feedback">
                                      {errors.branch}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    IFSC Code
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="text"
                                    placeholder="e.g., SBIN0123456"
                                    name="ifscCode"
                                    value={form.ifscCode}
                                    onChange={handleInputChange}
                                    className={
                                      errors.ifscCode ? "is-invalid" : ""
                                    }
                                    style={{ textTransform: "uppercase" }}
                                  />
                                  {errors.ifscCode && (
                                    <div className="invalid-feedback">
                                      {errors.ifscCode}
                                    </div>
                                  )}
                                  <small className="form-text text-muted">
                                    Format: 4 letters + 0 + 6 alphanumeric
                                    characters
                                  </small>
                                </FormGroup>
                              </Col>
                              <Col md={6}>
                                <FormGroup>
                                  <Label>
                                    Account Type
                                    <span className="text-danger">*</span>
                                  </Label>
                                  <Input
                                    type="select"
                                    name="accountType"
                                    value={form.accountType}
                                    onChange={handleInputChange}
                                    className={
                                      errors.accountType ? "is-invalid" : ""
                                    }
                                  >
                                    <option value="">
                                      Select Account Type
                                    </option>
                                    <option value="saving">Savings</option>
                                    <option value="current">Current</option>
                                  </Input>
                                  {errors.accountType && (
                                    <div className="invalid-feedback">
                                      {errors.accountType}
                                    </div>
                                  )}
                                </FormGroup>
                              </Col>
                            </>
                          )}
                          <Col md={12}>
                            <FormGroup
                              check
                              className="mt-4 d-flex justify-content-center"
                            >
                              <Label check>
                                <Input
                                  type="checkbox"
                                  name="defaultPayment"
                                  defaultChecked={form.defaultPayment}
                                  value={form.defaultPayment}
                                  onChange={handleCheckboxChange}
                                  style={{ fontSize: "20px" }}
                                />{" "}
                                Set as Default Payment Method
                              </Label>
                            </FormGroup>
                          </Col>
                          <Col md={12} className="mt-3">
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                type="button"
                                color="danger"
                                onClick={() => {
                                  setShowAddForm(false)
                                  setForm(initialFormState)
                                  setErrors({})
                                  setCardType("")
                                }}
                                className="btn-sm"
                              >
                                Cancel <i className="bx bx-x-circle"></i>
                              </Button>
                              <Button
                                type="submit"
                                color="primary"
                                className="btn-sm"
                              >
                                Submit <i className="bx bx-check-circle"></i>
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </CardBody>
                  </Card>
                </>
              )}
            </Col>
          </Row>

          {/* Payment Accounts Table */}
          <Row>
            <Col md={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-0">Receipt Accounts</h4>
                    <Button
                      color="primary"
                      onClick={() => {
                        setShowAddForm(!showAddForm)
                        setErrors({})
                        setCardType("")
                      }}
                      size="sm"
                    >
                      {showAddForm ? "Hide Form" : "Add New Account"}{" "}
                      <i className="bx bx-plus-circle"></i>
                    </Button>
                  </div>
                  <div className="table-responsive">
                    <Table hover bordered className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Sl.No</th>
                          <th>Type</th>
                          <th>Account Holder</th>
                          <th>Bank Name</th>
                          <th>Account/Card Number</th>
                          <th>UPI ID</th>
                          <th>Branch</th>
                          <th>IFSC Code</th>
                          <th>Account Type</th>
                          <th>Card Expiry</th>
                          <th>Default</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedAccounts.length > 0 ? (
                          displayedAccounts.map((account, index) => (
                            <tr key={account._id}>
                              <td>{pagesVisited + index + 1}</td>
                              <td>
                                <span>{account.type}</span>
                              </td>
                              <td>
                                {account.accountHolderName ||
                                  account.nameOnCard ||
                                  "-"}
                              </td>
                              <td>{account.bankName || "-"}</td>
                              <td>
                                {account.type === "Bankaccount"
                                  ? `${account.accountNumber}`
                                  : account.type === "Creditcard" ||
                                    account.type === "Debitcard"
                                  ? `${account.cardNumber}`
                                  : "-"}
                              </td>
                              <td>{account.upiId || "-"}</td>
                              <td>{account.branch || "-"}</td>
                              <td>{account.ifscCode || "-"}</td>
                              <td>
                                {account.accountType
                                  ? account.accountType
                                  : "-"}
                              </td>
                              <td>{account.cardExpiryDate || "-"}</td>
                              <td>
                                {account.defaultPayment ? (
                                  <b className="text-success">Yes</b>
                                ) : (
                                  <b className="text-danger">No</b>
                                )}
                              </td>
                              <td>
                                <div className="d-flex">
                                  {/* <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => openEditModal(account)}
                                    outline
                                  > */}
                                  <i
                                    className="bx bx-edit text-primary me-2"
                                    onClick={() => openEditModal(account)}
                                    style={{
                                      fontSize: "22px",
                                      cursor: "pointer",
                                    }}
                                  ></i>
                                  {/* </Button>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() =>
                                      deletePaymentAccount(account)
                                    }
                                    outline
                                  > */}
                                  <i
                                    className="bx bx-trash text-primary"
                                    onClick={() =>
                                      deletePaymentAccount(account)
                                    }
                                    style={{
                                      fontSize: "22px",
                                      cursor: "pointer",
                                    }}
                                  ></i>
                                  {/* </Button> */}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="12" className="text-center py-4">
                              <i className="bx bx-wallet text-muted display-4"></i>
                              <p className="mt-2">No receipt accounts found</p>
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => setShowAddForm(true)}
                                className="mt-2"
                                outline
                              >
                                Add Your First Account
                              </Button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>

                    {pageCount > 1 && (
                      <div
                        className="d-flex mt-3 mb-1 "
                        style={{ float: "right" }}
                      >
                        <ReactPaginate
                          previousLabel={"Previous"}
                          nextLabel={"Next"}
                          pageCount={pageCount}
                          onPageChange={changePage}
                          containerClassName={"pagination"}
                          previousLinkClassName={"page-link"}
                          nextLinkClassName={"page-link"}
                          disabledClassName={"disabled"}
                          activeClassName={"active"}
                          pageClassName={"page-item"}
                          pageLinkClassName={"page-link"}
                        />
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Edit Modal */}
          <Modal
            isOpen={showEditModal}
            toggle={() => setShowEditModal(false)}
            size="lg"
            centered
          >
            <ModalHeader toggle={() => setShowEditModal(false)}>
              Edit Receipt Account
            </ModalHeader>
            <Form onSubmit={handleEditSubmit}>
              <ModalBody>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label>
                        Payment Type <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        name="type"
                        value={editForm.type}
                        onChange={handleEditInputChange}
                        className={editErrors.type ? "is-invalid" : ""}
                      >
                        <option value="">Select Payment Type</option>
                        <option value="Upi">UPI</option>
                        <option value="Gpay">Google Pay</option>
                        <option value="Phonepe">Phonepe</option>
                        <option value="Creditcard">Credit Card</option>
                        <option value="Debitcard">Debit Card</option>
                        <option value="Bankaccount">Bank Account</option>
                      </Input>
                      {editErrors.type && (
                        <div className="invalid-feedback">
                          {editErrors.type}
                        </div>
                      )}
                    </FormGroup>
                  </Col>
                  {(editForm.type === "Upi" ||
                    editForm.type === "Gpay" ||
                    editForm.type === "Phonepe") && (
                    <Col md={6}>
                      <FormGroup>
                        <Label>
                          {editForm.type}
                          {editForm.type == "Upi" ? "UPI" : ""} Id
                          <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          placeholder="e.g., name@bank"
                          name="upiId"
                          value={editForm.upiId}
                          onChange={handleEditInputChange}
                          className={editErrors.upiId ? "is-invalid" : ""}
                        />
                        {editErrors.upiId && (
                          <div className="invalid-feedback">
                            {editErrors.upiId}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  )}
                  {(editForm.type === "Creditcard" ||
                    editForm.type === "Debitcard") && (
                    <>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Card Number <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter Card Number"
                            name="cardNumber"
                            value={editForm.cardNumber}
                            onChange={handleEditInputChange}
                            className={
                              editErrors.cardNumber ? "is-invalid" : ""
                            }
                            maxLength="19"
                          />
                          {editErrors.cardNumber && (
                            <div className="invalid-feedback">
                              {editErrors.cardNumber}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>
                            Expiry Date <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="month"
                            name="cardExpiryDate"
                            value={editForm.cardExpiryDate}
                            onChange={handleEditInputChange}
                            className={
                              editErrors.cardExpiryDate ? "is-invalid" : ""
                            }
                          />
                          {editErrors.cardExpiryDate && (
                            <div className="invalid-feedback">
                              {editErrors.cardExpiryDate}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>
                            CVV <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="password"
                            placeholder="CVV"
                            name="cvv"
                            value={editForm.cvv}
                            onChange={handleEditInputChange}
                            className={editErrors.cvv ? "is-invalid" : ""}
                            maxLength="4"
                          />
                          {editErrors.cvv && (
                            <div className="invalid-feedback">
                              {editErrors.cvv}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Name on Card <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter Name on Card"
                            name="nameOnCard"
                            value={editForm.nameOnCard}
                            onChange={handleEditInputChange}
                            className={
                              editErrors.nameOnCard ? "is-invalid" : ""
                            }
                          />
                          {editErrors.nameOnCard && (
                            <div className="invalid-feedback">
                              {editErrors.nameOnCard}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </>
                  )}
                  {editForm.type === "Bankaccount" && (
                    <>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Bank Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter Bank Name"
                            name="bankName"
                            value={editForm.bankName}
                            onChange={handleEditInputChange}
                            className={editErrors.bankName ? "is-invalid" : ""}
                          />
                          {editErrors.bankName && (
                            <div className="invalid-feedback">
                              {editErrors.bankName}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Account Holder Name
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter Account Holder Name"
                            name="accountHolderName"
                            value={editForm.accountHolderName}
                            onChange={handleEditInputChange}
                            className={
                              editErrors.accountHolderName ? "is-invalid" : ""
                            }
                          />
                          {editErrors.accountHolderName && (
                            <div className="invalid-feedback">
                              {editErrors.accountHolderName}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Account Number
                            <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter Account Number"
                            name="accountNumber"
                            value={editForm.accountNumber}
                            onChange={handleEditInputChange}
                            className={
                              editErrors.accountNumber ? "is-invalid" : ""
                            }
                            maxLength="18"
                          />
                          {editErrors.accountNumber && (
                            <div className="invalid-feedback">
                              {editErrors.accountNumber}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Branch <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter Branch"
                            name="branch"
                            value={editForm.branch}
                            onChange={handleEditInputChange}
                            className={editErrors.branch ? "is-invalid" : ""}
                          />
                          {editErrors.branch && (
                            <div className="invalid-feedback">
                              {editErrors.branch}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            IFSC Code <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            placeholder="Enter IFSC Code"
                            name="ifscCode"
                            value={editForm.ifscCode}
                            onChange={handleEditInputChange}
                            className={editErrors.ifscCode ? "is-invalid" : ""}
                            style={{ textTransform: "uppercase" }}
                          />
                          {editErrors.ifscCode && (
                            <div className="invalid-feedback">
                              {editErrors.ifscCode}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>
                            Account Type <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="select"
                            name="accountType"
                            value={editForm.accountType}
                            onChange={handleEditInputChange}
                            className={
                              editErrors.accountType ? "is-invalid" : ""
                            }
                          >
                            <option value="">Select Account Type</option>
                            <option value="saving">Savings</option>
                            <option value="current">Current</option>
                          </Input>
                          {editErrors.accountType && (
                            <div className="invalid-feedback">
                              {editErrors.accountType}
                            </div>
                          )}
                        </FormGroup>
                      </Col>
                    </>
                  )}
                  <Col md={12}>
                    <FormGroup
                      check
                      className="mt-4 d-flex justify-content-center"
                    >
                      <Label check>
                        <Input
                          type="checkbox"
                          name="defaultPayment"
                          defaultChecked={editForm.defaultPayment}
                          value={editForm.defaultPayment}
                          onChange={handleEditCheckboxChange}
                        />{" "}
                        Set as Default Payment Method
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button
                  type="button"
                  color="danger"
                  onClick={() => setShowEditModal(false)}
                  className="btn-sm"
                >
                  Cancel <i className="bx bx-x-circle"></i>
                </Button>
                <Button type="submit" color="primary" className="btn-sm">
                  Update <i className="bx bx-check-circle"></i>
                </Button>
              </ModalFooter>
            </Form>
          </Modal>
        </div>
        <ToastContainer position="top-right" autoClose={3000} />
      </div>
    </React.Fragment>
  )
}

export default PaymentsAccounts
